import { ArrowSquareOut, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { IconButton } from '@/components/primitives/icon-button';
import { isDesktop } from './index';
import { checkForUpdate, type UpdateAvailable } from './update-check';

/** 使用者關掉提示後，同一個版本不再打擾。 */
const DISMISSED_KEY = 'ui.update.dismissedVersion';

/**
 * 桌面版的更新提示（desktop-app.md 5.5）。
 *
 * **只在畫面角落顯示一行提示與下載連結**，不自動下載、不阻擋使用。
 * 網頁版完全不渲染——它每次重新整理本來就是最新的。
 */
export function UpdateNotice() {
  const [update, setUpdate] = useState<UpdateAvailable | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isDesktop()) return;

    let cancelled = false;
    void checkForUpdate().then((result) => {
      if (cancelled || !result) return;
      // 抓不到一律是 null，這裡不需要 catch：checkForUpdate 永遠不丟例外。
      if (readDismissed() === result.latest) return;
      setUpdate(result);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!update || dismissed) return null;

  return (
    <aside
      aria-label="有新版本"
      className="fixed bottom-6 left-6 z-[60] flex max-w-sm items-start gap-3 rounded-[var(--radius-panel)] border border-border bg-surface-raised px-4 py-3 shadow-[var(--shadow-raised)]"
    >
      <div className="min-w-0 space-y-1">
        <p className="text-[0.875rem] font-medium">有新版本 {update.latest}</p>
        <p className="text-[0.8125rem] leading-relaxed text-text-muted">
          目前是 {update.current}。
          {update.notes ?? '下載新版後直接覆蓋舊的 exe 就好，資料不受影響。'}
        </p>
        <a
          href={update.exeUrl}
          className="inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-accent hover:text-accent-strong"
        >
          下載新版
          <ArrowSquareOut size={14} />
        </a>
      </div>
      <IconButton
        label="不再提示這個版本"
        icon={<X size={16} />}
        className="size-8 shrink-0"
        onClick={() => {
          writeDismissed(update.latest);
          setDismissed(true);
        }}
      />
    </aside>
  );
}

function readDismissed(): string | null {
  try {
    return localStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(version: string): void {
  try {
    localStorage.setItem(DISMISSED_KEY, version);
  } catch {
    // 記不住就下次再提示一次，不是問題。
  }
}
